import { Component, Input, inject } from "@angular/core";
import { Observable, NEVER } from "rxjs";
import { switchMap, map } from "rxjs/operators";
import { CstDialogResult } from "src/app/common-components/dialogs/cst-dialog-enums";
import { CstDialogsService } from "src/app/common-components/dialogs/cst-dialogs.service";
import {
  ICstFolderDocumentTreeNodeFolder,
  ICstFolderDocumentTreeNode,
  CstFolderDocumentTreeNodeType,
  CstFolderDocumentTreeDatasource,
} from "src/app/common-components/folder-document-tree/models";
import { ICstGuiCommand } from "src/app/common-components/list-view/models";
import { CstResponsivePopupService } from "src/app/common-components/resp-popup/services/resp-popup.service";
import {
  InfoportalFoldersClientProvider,
  ContractFolderLookupDto,
  ContractFolderDeleteCommand,
  DokumentLookupDetailDto,
} from "src/app/web-api/web-api";
import {
  IInfoportalApiContractFolderCreateDialogData,
  InfoportalApiContractFolderCreateDialogComponent,
} from "../../dialogs/contracts/contract-folder-create/contract-folder-create-dialog.component";
import {
  IInfoportalApiContractFolderUpdateDialogData,
  InfoportalApiContractFolderUpdateDialogComponent,
} from "../../dialogs/contracts/contract-folder-update/contract-folder-update-dialog.component";
import {
  InfoportalDocumentCreateComponent,
  IInfoportalDocumentCreateData,
} from "../documents/insert/document-create.component";

@Component({
    selector: "app-infoprtal-contract-documents-tree",
    templateUrl: "./contract-documents-tree.component.html",
    styleUrls: ["./contract-documents-tree.component.scss"],
    standalone: false
})
export class InfoportalApiContractDocumentsTreeComponent {
  private _foldersClient = inject(InfoportalFoldersClientProvider);
  private _dlg = inject(CstDialogsService);
  private _popup = inject(CstResponsivePopupService);

  private _dataSource: CstFolderDocumentTreeDatasource = null;
  private _contractNo = "";

  private _folderActions: ICstGuiCommand<ICstFolderDocumentTreeNodeFolder>[] = [
    {
      execute: this.onDocumentCreate.bind(this),
      canExecute: this.canDocumentCreate.bind(this),
      label: $localize`:@@InfoportalAPI_Contracts_Documents_DocumentCreate:New Document`,
      icon: "note_add",
    },
    {
      execute: this.onFolderUpdate.bind(this),
      canExecute: this.canFolderUpdate.bind(this),
      label: $localize`:@@InfoportalAPI_Contracts_Documents_FolderUpdate:Update Folder`,
      icon: "edit",
    },
    {
      execute: this.onFolderDelete.bind(this),
      canExecute: this.canFolderDelete.bind(this),
      label: $localize`:@@InfoportalAPI_Contracts_Documents_FolderDelete:Delete Folder`,
      icon: "delete",
    },
  ];

  @Input() set contractNo(value: string) {
    if (!value || value === this._contractNo) {
      return;
    }
    this._contractNo = value;
    this._loadFolders();
  }

  public onDataSourceInit(dataSource: CstFolderDocumentTreeDatasource) {
    this._dataSource = dataSource;
    this._loadFolders();
  }
  public canDocumentCreate(node: ICstFolderDocumentTreeNode) {
    return (
      node.identifier &&
      typeof node.identifier === "string" &&
      node.type === CstFolderDocumentTreeNodeType.Folder
    );
  }

  public onDocumentCreate(node: ICstFolderDocumentTreeNode) {
    if (!this.canDocumentCreate(node)) {
      return;
    }

    this._popup
      .open<
        InfoportalDocumentCreateComponent,
        IInfoportalDocumentCreateData,
        DokumentLookupDetailDto[]
      >(InfoportalDocumentCreateComponent, {
        data: {
          folderSource: "contract",
          folderID: node.identifier as string,
          displayInternToggle: false,
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (!result) {
          return;
        }
        for (const doc of result) {
          this._dataSource.addSubNode(
            this._mapDocumentToDocumentNode(doc),
            (n) => n.identifier === node.identifier
          );
        }
      });
  }

  public canFolderCreate(contractNo: string) {
    return !!contractNo;
  }
  public onFolderCreate(contractNo: string) {
    if (!this.canFolderCreate(contractNo)) {
      return;
    }
    this._popup
      .open<
        InfoportalApiContractFolderCreateDialogComponent,
        IInfoportalApiContractFolderCreateDialogData,
        ContractFolderLookupDto
      >(InfoportalApiContractFolderCreateDialogComponent, {
        data: { contractNo },
      })
      .afterClosed()
      .subscribe((result) => {
        if (!result) {
          return;
        }
        this._dataSource.addNode(this._mapContractFolderToFolderNode(result));
      });
  }

  public canFolderUpdate(node: ICstFolderDocumentTreeNodeFolder) {
    return node.identifier && typeof node.identifier === "string";
  }
  public onFolderUpdate(node: ICstFolderDocumentTreeNodeFolder) {
    if (!this.canFolderUpdate(node)) {
      return;
    }
    this._popup
      .open<
        InfoportalApiContractFolderUpdateDialogComponent,
        IInfoportalApiContractFolderUpdateDialogData,
        ContractFolderLookupDto
      >(InfoportalApiContractFolderUpdateDialogComponent, {
        data: { id: node.identifier as string },
      })
      .afterClosed()
      .pipe(
        switchMap((result) => {
          if (!result) {
            return NEVER;
          }
          return this._dataSource
            .findNode((node) => node.identifier === result.id)
            .pipe(map((node) => ({ node, result })));
        })
      )
      .subscribe(({ node, result }) => {
        node.label = result.label;
        node.description = result.description;
        node.date = result.changed;
      });
  }

  public canFolderDelete(node: ICstFolderDocumentTreeNodeFolder) {
    return node.identifier && typeof node.identifier === "string";
  }
  public onFolderDelete(node: ICstFolderDocumentTreeNodeFolder) {
    if (!this.canFolderDelete(node)) {
      return;
    }
    this._dlg
      .AskYesNo(
        $localize`:@@InfoportalAPI_Contracts_Documents_FolderDeleteConfirm:Do you really want to delete this folder?`
      )
      .pipe(
        switchMap((dlgResult) => {
          if (dlgResult !== CstDialogResult.Yes) {
            return NEVER;
          }
          return this._foldersClient.contractFolderDelete(
            new ContractFolderDeleteCommand({ id: node.identifier as string })
          );
        })
      )
      .subscribe(() => this._dataSource.remove(node));
  }

  private _loadFolders() {
    if (!this._dataSource || !this._contractNo) {
      return;
    }
    this._foldersClient
      .contractFoldersGet(this._contractNo)
      .subscribe((result) => {
        if (this._contractNo) {
          this._dataSource.data = result.data.map(
            this._mapContractFolderToFolderNode.bind(this)
          );
          return;
        }
        this._dataSource.data = result.data.map(
          this._mapContractFolderToFolderNode.bind(this)
        );
      });
  }

  private _mapContractFolderToFolderNode(folder: ContractFolderLookupDto) {
    return this._dataSource.createFolder({
      identifier: folder.id,
      label: folder.label,
      description: folder.description,
      date: folder.changed,
      children:
        folder.documentCount > 0
          ? this._loadDocumentsForFolder(folder.id)
          : null,
      actions: this._folderActions,
    });
  }

  private _loadDocumentsForFolder(
    id: string
  ): Observable<ICstFolderDocumentTreeNode[]> {
    return this._foldersClient
      .contractFolderDocumentsGet(id)
      .pipe(
        map((result) =>
          result.data.map((x) => this._mapDocumentToDocumentNode(x))
        )
      );
  }
  private _mapDocumentToDocumentNode(x: DokumentLookupDetailDto) {
    return this._dataSource.createFile({
      identifier: x.id,
      label: x.filename,
      description: x.bezeichnung,
      fileSize: x.fileSize,
      date: x.lastChanged,
    });
  }
}
